import React, { useState, useRef } from 'react';
import { importarJornada, downloadModeloExcel } from '../services/api';

export default function ImportarJornada() {
  const [arquivo, setArquivo] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const inputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setErro('Formato inválido. Use arquivos .xlsx ou .xls');
      return;
    }
    setArquivo(file);
    setErro('');
    setResultado(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const importar = async () => {
    if (!arquivo) { setErro('Selecione um arquivo!'); return; }
    setLoading(true);
    setErro('');
    setResultado(null);

    const fd = new FormData();
    fd.append('arquivo', arquivo);

    try {
      const res = await importarJornada(fd);
      setResultado(res.data);
      setArquivo(null);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao importar arquivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h4 className="fw-bold mb-4">
        <i className="bi bi-file-earmark-arrow-up me-2 text-primary"></i>
        Importar Jornada por Excel
      </h4>

      <div className="row g-4">
        {/* Instruções */}
        <div className="col-lg-4">
          <div className="card-ponto p-4 h-100">
            <h6 className="fw-bold mb-3 text-primary">
              <i className="bi bi-info-circle me-2"></i>Como funciona
            </h6>
            <ol className="ps-3" style={{ lineHeight: 2 }}>
              <li>Baixe o modelo Excel</li>
              <li>Preencha com os dados dos funcionários</li>
              <li>Salve e faça o upload aqui</li>
              <li>Clique em <strong>Importar</strong></li>
            </ol>

            <hr />
            <h6 className="fw-bold mb-2 text-primary">Colunas do Excel:</h6>
            <ul className="list-unstyled" style={{ fontSize: '0.85rem' }}>
              {[
                ['matricula *', 'Código único'],
                ['nome *', 'Nome completo'],
                ['departamento', 'Nome do setor'],
                ['cargo', 'Cargo'],
                ['hora_entrada *', 'Ex: 08:00'],
                ['hora_inicio_almoco', 'Ex: 12:00'],
                ['hora_fim_almoco', 'Ex: 13:00'],
                ['hora_saida *', 'Ex: 17:00'],
                ['dias_semana', 'SEG,TER,QUA,QUI,SEX'],
                ['carga_horaria', 'Ex: 8 (padrão)'],
              ].map(([col, desc]) => (
                <li key={col} className="mb-1">
                  <code style={{ fontSize: '0.8rem' }}>{col}</code>
                  <span className="text-muted ms-2">{desc}</span>
                </li>
              ))}
            </ul>

            <button
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%', marginTop: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', color: '#15803d', fontSize: '0.72rem', fontWeight: 600 }}
              onClick={downloadModeloExcel}
            >
              <i className="bi bi-download"></i>
              Baixar Modelo Excel
            </button>
          </div>
        </div>

        {/* Upload */}
        <div className="col-lg-8">
          <div className="card-ponto p-4">
            <h6 className="fw-bold mb-3">Selecionar Arquivo</h6>

            <div
              className={`upload-zone ${dragging ? 'dragover' : ''} ${arquivo ? 'border-success' : ''}`}
              onClick={() => inputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                className="d-none"
                onChange={e => handleFile(e.target.files[0])}
              />
              {arquivo ? (
                <>
                  <i className="bi bi-file-earmark-excel" style={{ fontSize: '3rem', color: '#16a34a' }}></i>
                  <div className="fw-semibold mt-2 text-success">{arquivo.name}</div>
                  <small className="text-muted">
                    {(arquivo.size / 1024).toFixed(1)} KB · Clique para trocar o arquivo
                  </small>
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload"></i>
                  <div className="fw-semibold mt-2">Arraste o arquivo aqui</div>
                  <small className="text-muted">ou clique para selecionar · .xlsx ou .xls</small>
                </>
              )}
            </div>

            {erro && (
              <div className="alert alert-danger mt-3 alert-importacao">
                <i className="bi bi-exclamation-triangle me-2"></i>{erro}
              </div>
            )}

            <button
              className="btn btn-primary-custom w-100 mt-3 py-3"
              onClick={importar}
              disabled={loading || !arquivo}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2" />Importando...</>
              ) : (
                <><i className="bi bi-upload me-2" />Importar Jornada</>
              )}
            </button>
          </div>

          {/* Resultado */}
          {resultado && (
            <div className="card-ponto p-4 mt-4">
              <h6 className="fw-bold mb-3">
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Resultado da Importação
              </h6>
              <div className="row g-3 mb-3">
                <div className="col-4">
                  <div className="text-center p-3 rounded" style={{ background: '#f0f4f8' }}>
                    <div className="fs-3 fw-bold">{resultado.total}</div>
                    <small className="text-muted">Total de Linhas</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center p-3 rounded" style={{ background: '#d1fae5' }}>
                    <div className="fs-3 fw-bold text-success">{resultado.importados}</div>
                    <small className="text-success">Importados</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center p-3 rounded" style={{ background: resultado.erros > 0 ? '#fee2e2' : '#f0f4f8' }}>
                    <div className={`fs-3 fw-bold ${resultado.erros > 0 ? 'text-danger' : ''}`}>{resultado.erros}</div>
                    <small className={resultado.erros > 0 ? 'text-danger' : 'text-muted'}>Erros</small>
                  </div>
                </div>
              </div>

              <div className={`alert ${resultado.erros === 0 ? 'alert-success' : 'alert-warning'} alert-importacao`}>
                {resultado.mensagem}
              </div>

              {resultado.log_erros && resultado.log_erros.length > 0 && (
                <div>
                  <h6 className="fw-bold text-danger">Log de Erros:</h6>
                  <div style={{ background: '#1e1e1e', color: '#f8f8f2', borderRadius: 8, padding: 16, fontSize: '0.8rem', maxHeight: 200, overflowY: 'auto' }}>
                    {resultado.log_erros.map((e, i) => (
                      <div key={i} className="mb-1">• {e}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
